import {
    createContext, useState
} from 'react';
import useKBAction from '../hooks/useKBAction';

/**
 * Context for the whole Sidebar state. Contains the state of the KB Actions which involves the Processing and Querying.
 * @type {import('react').Context<KBSidebarContextType>}
 */
export const SidebarContext = createContext(null);
/**
 * Context for the whole Sidebar state. Contains the state of the KB Actions which involves the Processing and Querying.
 * @typedef {Object} KBSidebarContextType
 * @property {boolean} l2IsProcessing - L2 processing state. True if L1 is still processing
 * @property {boolean} isExporting - Export processing state. True if still exporting
 * @property {import("../components/KBDropdown").KBDropdownOption} processType - Selected process type
 * @property {import("../components/KBDropdown").KBDropdownOption} levelType - Selected level type
 * @property {Object} l1Filter - Selected L1 filter options
 * @property {Object} l2Filter - Selected L2 filter options
 * @property {Object} exportFilter - Selected export filter options
 * @property {Object} contextualRetrievalMode - Selected contextual retrieval mode
 * @property {string} searchQuery - Current search query string
 * @property {Object} searchType - Selected search type
 * @property {boolean} rerank - Whether to rerank the search results
 * @property {number} topK - Number of top results to return
 * @property {number} topN - Number of top results to consider for reranking
 * @property {function} setProcessType - Function to set the process type
 * @property {function} setLevelType - Function to set the level type
 * @property {function} setContextualRetrievalMode - Function to set the contextual retrieval mode
 * @property {function} setL1Filter - Function to set the L1 filter options
 * @property {function} setL2Filter - Function to set the L2 filter options
 * @property {function} setExportFilter - Function to set the export filter options
 * @property {function} setSearchQuery - Function to set the search query
 * @property {function} executeL1Processing - Function to trigger the L1 processing
 * @property {function} executeL2Processing - Function to trigger the L2 processing
 * @property {function} executeExport - Function to trigger the export processing
 * @property {function} sendQuery - Function to query documents
 * @property {function} loadMoreQuery - Function to Load more query results
 * @property {function} setSearchType - Function to set the search type
 * @property {function} setRerank - Function to set the rerank option
 * @property {function} setTopK - Function to set the number of top results
 * @property {function} setTopN - Function to set the number of results for reranking
 * @property {function} setQueryLoading - Function to set the loading state of query
 * @property {boolean} queryLoading - The loading state of query
 * @property {React.RefObject} QueryResultDetails - A ref that holds the query result details.
 * @property {Array} queryResults - The list of query results
 * @property {Object} selectedQuery - The chunk ID of h selected query result.
 * @property {Function} setSelectedQuery - The settter for the selected query result.
 * @property {function():void} resetQueryResults Resets the query results to an empty array
 * @property {function(boolean):void} setEditDialogVisible Sets the visibility of the edit dialog
 * @property {boolean} editDialogVisible Sets the visibility of the edit dialog
 *
 */

/**
 * Provider component for the Sidebar context. Manages the state of knowledge base processing actions.
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components that will have access to the context
 * @returns {JSX.Element} Context provider wrapper
 */
export default function KBSidebarContext({
    children
}) {
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const {
        // States
        l2IsProcessing,
        isExporting,
        processType,
        levelType,
        l1Filter,
        l2Filter,
        exportFilter,
        contextualRetrievalMode,
        searchQuery,
        searchType,
        rerank,
        topK,
        topN,
        queryLoading,
        queryResults,
        selectedQuery,
        l2ModelType,
        l2Instruction,
        l2BatchSize,
        isLoadingInstructions,

        // Setters
        setProcessType,
        setLevelType,
        setContextualRetrievalMode,
        setL1Filter,
        setL2Filter,
        setExportFilter,
        setSearchQuery,
        setSearchType,
        setRerank,
        setTopK,
        setTopN,
        setQueryLoading,
        setL2ModelType,
        setL2Instruction,
        setL2BatchSize,
        setIsLoadingInstructions,

        // Action functions
        executeL1Processing,
        executeL2Processing,
        executeExport,
        sendQuery,
        loadMoreQuery,
        resetQueryResults,
        setSelectedQuery,

        // Refs
        QueryResultDetails
    } = useKBAction();

    return (
        <SidebarContext.Provider value={{
            l2IsProcessing,
            isExporting,
            processType,
            levelType,
            l1Filter,
            l2Filter,
            exportFilter,
            contextualRetrievalMode,
            searchQuery,
            searchType,
            rerank,
            topK,
            topN,
            queryLoading,
            queryResults,
            selectedQuery,
            editDialogVisible,
            QueryResultDetails,
            l2ModelType,
            l2Instruction,
            l2BatchSize,
            isLoadingInstructions,

            setProcessType,
            setLevelType,
            setContextualRetrievalMode,
            setL1Filter,
            setL2Filter,
            setExportFilter,
            setSearchQuery,
            executeL1Processing,
            executeL2Processing,
            executeExport,
            sendQuery,
            loadMoreQuery,
            setSearchType,
            setRerank,
            setTopK,
            setTopN,
            setQueryLoading,
            setSelectedQuery,
            resetQueryResults,
            setEditDialogVisible,
            setL2ModelType,
            setL2Instruction,
            setL2BatchSize,
            setIsLoadingInstructions,
        }}>
            {children}
        </SidebarContext.Provider>
    );

}